/**
 * AssetsViewSwitch — "View by Industry | View by Topic" segmented control.
 * Owns only the toggle; each branch renders its own matrix
 * (AssetsIndustryMatrix / AssetsTopicMatrix) in AssetsOverviewPage, so this
 * switch stays decoupled from either view's layout.
 */
import { SegmentedControl } from '@amzn/awsui-components-react';

export type AssetsView = 'industry' | 'topic';

const VIEWS: AssetsView[] = ['industry', 'topic'];
const DEFAULT_VIEW: AssetsView = 'industry';

// ── Persistence (survive navigating away + back, same session) ───────────────
// Mirrors the origin-filter persistence: a single string id in sessionStorage,
// seeded on mount so the chosen view sticks across in-app navigation.
const VIEW_STORAGE_KEY = 'ivt.assets.view';

export function loadAssetsView(): AssetsView {
  try {
    const v = sessionStorage.getItem(VIEW_STORAGE_KEY);
    if (v && VIEWS.includes(v as AssetsView)) return v as AssetsView; // only known ids
  } catch {
    /* ignore */
  }
  return DEFAULT_VIEW;
}

export function saveAssetsView(v: AssetsView): void {
  try {
    sessionStorage.setItem(VIEW_STORAGE_KEY, v);
  } catch {
    /* ignore */
  }
}

interface Props {
  value: AssetsView;
  onChange: (v: AssetsView) => void;
}

export default function AssetsViewSwitch({ value, onChange }: Props) {
  return (
    <SegmentedControl
      selectedId={value}
      onChange={({ detail }) => onChange(detail.selectedId as AssetsView)}
      label="Assets 视图切换"
      options={[
        { id: 'industry', text: 'View by Industry' },
        { id: 'topic', text: 'View by Topic' },
      ]}
    />
  );
}
